import React from "react";
import {createStyles, makeStyles, Theme} from '@material-ui/core/styles';
import {useDispatch} from 'react-redux';
import {
  Checkbox,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Typography
} from "@material-ui/core";
import {
  DenseIcon,
  DenseMenu,
  PromptDialog,
  TextDialog
} from "../components";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import BookmarkIcon from '@material-ui/icons/Bookmark';
import SyncIcon from '@material-ui/icons/Sync';
import {
  deleteBookmark,
  updateBookmark,
  Bookmark
} from "../redux/bookmarks-slice";
import { useTranslation } from "react-i18next";
import { toast } from "../app";
import { getStoredState } from ".";
import LocalScene from "../viewer/js/projections";
import { getVisibleProjectAssets } from "../asset-drawer";
import { useAuth, useContextMenu, useDialog, useViewer } from "../hooks";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    itemList: {
      paddingLeft: theme.spacing(4),
      display: "inline-block"
    },
    assetInfo: {
      display: 'flex',
      margin: '0px'
    },
    itemText: {
      wordBreak: "break-word",
      overflow: "hidden"
    },
    checkboxParent: {
      padding: "0px",
      height: "100%"
    },
    assetCheckbox: {
      position: "relative",
      transform: "translateY(0%)",
    }
  })
);

interface BookmarkItemProps {
  bookmark: Bookmark;
  loadBookmark: (bookmarkID: string) => void
}

export const BookmarkItem = (props: BookmarkItemProps) => {
  const {bookmark, loadBookmark} = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const {viewer} = useViewer();
  const {permissions} = useAuth();
  const {t} = useTranslation();
  const contextMenu = useContextMenu();
  const renameDialog = useDialog();
  const deleteDialog = useDialog();
  const updateDialog = useDialog();

  const handleBookmarkClick = (event) => {
    const isCheckBox = (event.target.type === "checkbox");
    if (isCheckBox) return;

    loadBookmark(bookmark.id);
  };

  const handleVisibilityClick = () => {
    dispatch(updateBookmark({
      bookmarkID: bookmark.id,
      visible: !bookmark.visible
    }));
  };

  const handleDeleteSubmit = () => {
    dispatch(deleteBookmark(bookmark.id));
    deleteDialog.handleClose();
  };

  const handleDeleteClick = () => {
    contextMenu.handleClose();
    deleteDialog.handleOpen();
  };

  const handleRenameSubmit = name => {
    dispatch(updateBookmark({bookmarkID: bookmark.id, name}));
    renameDialog.handleClose();
  };

  const handleRenameClick = () => {
    contextMenu.handleClose();
    renameDialog.handleOpen();
  };

  const handleUpdateSubmit = () => {
    updateDialog.handleClose();

    if (!LocalScene.initialized) {
      toast.warning(t('bookmarks.invalid_position'));
      return;
    }

    const position = viewer.getCameraPosition().toArray();
    const {settings, aerialState, sceneState} = getStoredState();
    const visibleAssets = getVisibleProjectAssets();

    dispatch(updateBookmark({
      bookmarkID: bookmark.id,
      position,
      settings,
      visibleAssets,
      aerialState,
      sceneState
    }));

    toast.success(t('bookmarks.updated_bookmark'));
  };

  const handleUpdateClick = () => {
    contextMenu.handleClose();
    updateDialog.handleOpen();
  };

  return (
    <React.Fragment>
      <ListItem
        button
        className={classes.itemList}
        onClick={handleBookmarkClick}
        onContextMenu={contextMenu.handleOpen}
      >
        <div className={classes.assetInfo}>
          <DenseIcon centered>
            <BookmarkIcon fontSize="small"/>
          </DenseIcon>

          <ListItemText
            className={classes.itemText}
            primary={bookmark.name}
          />

          <ListItemSecondaryAction
            className={classes.assetCheckbox}
          >
            <Checkbox
              edge="end"
              size="small"
              className={classes.checkboxParent}
              onChange={handleVisibilityClick}
              checked={bookmark.visible}
            />
          </ListItemSecondaryAction>

        </div>
      </ListItem>

      <DenseMenu
        open={contextMenu.open}
        onClose={contextMenu.handleClose}
        anchorPosition={contextMenu.anchorPosition}
      >
        <MenuItem
          onClick={handleRenameClick}
          disabled={!permissions.edit}
        >
          <DenseIcon>
            <EditIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t('buttons.rename')}</Typography>
        </MenuItem>

        <MenuItem
          onClick={handleUpdateClick}
          disabled={!permissions.edit}
        >
          <DenseIcon>
            <SyncIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t('buttons.update')}</Typography>
        </MenuItem>

        <MenuItem
          onClick={handleDeleteClick}
          disabled={!permissions.delete}
        >
          <DenseIcon>
            <DeleteIcon fontSize="small"/>
          </DenseIcon>
          <Typography variant="inherit">{t('buttons.delete')}</Typography>
        </MenuItem>

      </DenseMenu>

      {/* Rename Dialog */}
      <TextDialog
        open={renameDialog.open}
        onCancel={renameDialog.handleClose}
        onSubmit={handleRenameSubmit}
        title={t('asset.rename-title')}
        prompt={t('asset.rename-prompt')}
        placeholder={bookmark.name}
        label={t('asset.rename-label')}
      />

      {/* Update Dialog */}
      <PromptDialog
        open={updateDialog.open}
        onCancel={updateDialog.handleClose}
        onSubmit={handleUpdateSubmit}
        title={t('bookmarks.update_bookmark')}
        prompt={t('bookmarks.do_you_want_to_update_this_bookmark')}
        button={t('buttons.update')}
      />

      {/* Delete Dialog */}
      <PromptDialog
        open={deleteDialog.open}
        onCancel={deleteDialog.handleClose}
        onSubmit={handleDeleteSubmit}
        title={t('bookmarks.delete_bookmark')}
        prompt={t('bookmarks.do_you_want_to_delete_this_bookmark')}
        button={t("buttons.delete")}
      />
    </React.Fragment>
  );
};